/**
 * Login Page Component
 *
 * Handles user authentication and registration.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  authService,
  type LoginFailureReason,
  type UserRole,
  USER_ROLES,
} from "../services/auth";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";

type AuthMode = "login" | "register" | "forgot";

@customElement("login-page")
export class LoginPage extends LitElement {
  @state() private mode: AuthMode = "login";
  @state() private email = "";
  @state() private password = "";
  @state() private confirmPassword = "";
  @state() private displayName = "";
  @state() private isLoading = false;
  @state() private error = "";
  @state() private successMessage = "";
  @state() private staySignedIn = true;
  @state() private selectedRoles: Set<UserRole> = new Set([
    USER_ROLES.DIABETIC,
  ]);

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 1rem;
    }

    .card {
      background: white;
      border-radius: 1rem;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
      padding: 2rem;
      width: 100%;
      max-width: 400px;
    }

    .logo {
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .logo h1 {
      margin: 0;
      font-size: 2rem;
      color: #0d6efd;
      font-weight: 700;
    }

    .logo p {
      margin: 0.5rem 0 0 0;
      color: #6c757d;
      font-size: 0.875rem;
    }

    h2 {
      margin: 0 0 1.5rem 0;
      font-size: 1.25rem;
      text-align: center;
      color: #333;
    }

    .form-group {
      margin-bottom: 1rem;
    }

    sl-input {
      width: 100%;
    }

    sl-input::part(base) {
      border-radius: 0.5rem;
    }

    .checkbox-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 1rem 0;
      font-size: 0.875rem;
      color: #666;
    }

    .checkbox-group input {
      width: 1rem;
      height: 1rem;
    }

    .role-selection {
      margin: 1rem 0;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    .role-selection-label {
      font-weight: 500;
      font-size: 0.875rem;
      color: #333;
      margin-bottom: 0.75rem;
      display: block;
    }

    .role-options {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .role-option {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .role-option sl-checkbox::part(label) {
      font-size: 0.875rem;
    }

    .role-description {
      font-size: 0.75rem;
      color: #6c757d;
      margin-left: 1.5rem;
    }

    sl-button {
      width: 100%;
    }

    sl-button::part(base) {
      border-radius: 0.5rem;
    }

    .error-alert {
      margin-bottom: 1rem;
    }

    .success-alert {
      margin-bottom: 1rem;
    }

    .links {
      margin-top: 1.5rem;
      text-align: center;
    }

    .links a {
      color: #0d6efd;
      text-decoration: none;
      font-size: 0.875rem;
      cursor: pointer;
    }

    .links a:hover {
      text-decoration: underline;
    }

    .divider {
      margin: 1rem 0;
      color: #999;
      font-size: 0.875rem;
    }

    .offline-notice {
      background: #fef3cd;
      border: 1px solid #ffc107;
      border-radius: 0.5rem;
      padding: 0.75rem;
      margin-bottom: 1rem;
      font-size: 0.875rem;
      color: #856404;
      text-align: center;
    }
  `;

  render() {
    const isOffline = !navigator.onLine;

    return html`
      <div class="container">
        <div class="card">
          <div class="logo">
            <h1>DAFNE Log</h1>
            <p>Diabetes Management Made Simple</p>
          </div>

          ${isOffline
            ? html`
                <div class="offline-notice">
                  <sl-icon name="wifi-off"></sl-icon>
                  You're offline. Login when connected.
                </div>
              `
            : ""}
          ${this.error
            ? html`
                <sl-alert variant="danger" open class="error-alert">
                  <sl-icon slot="icon" name="exclamation-circle"></sl-icon>
                  ${this.error}
                </sl-alert>
              `
            : ""}
          ${this.successMessage
            ? html`
                <sl-alert variant="success" open class="success-alert">
                  <sl-icon slot="icon" name="check-circle"></sl-icon>
                  ${this.successMessage}
                </sl-alert>
              `
            : ""}
          ${this.mode === "login"
            ? this.renderLogin()
            : this.mode === "register"
              ? this.renderRegister()
              : this.renderForgotPassword()}
        </div>
      </div>
    `;
  }

  private renderLogin() {
    return html`
      <h2>Sign In</h2>
      <form @submit=${this.handleLogin}>
        <div class="form-group">
          <sl-input
            type="email"
            name="email"
            autocomplete="username"
            label="Email"
            placeholder="you@example.com"
            required
            .value=${this.email}
            @sl-input=${(e: Event) =>
              (this.email = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <div class="form-group">
          <sl-input
            type="password"
            name="password"
            autocomplete="current-password"
            label="Password"
            placeholder="Enter your password"
            required
            .value=${this.password}
            @sl-input=${(e: Event) =>
              (this.password = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <div class="checkbox-group">
          <input
            type="checkbox"
            id="staySignedIn"
            .checked=${this.staySignedIn}
            @change=${(e: Event) =>
              (this.staySignedIn = (e.target as HTMLInputElement).checked)}
          />
          <label for="staySignedIn">Keep me signed in</label>
        </div>
        <sl-button
          type="submit"
          variant="primary"
          ?loading=${this.isLoading}
          ?disabled=${!navigator.onLine}
        >
          ${this.isLoading ? "Signing in..." : "Sign In"}
        </sl-button>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("forgot")}>Forgot password?</a>
        <div class="divider">or</div>
        <a @click=${() => this.switchMode("register")}>Create an account</a>
      </div>
    `;
  }

  private renderRegister() {
    return html`
      <h2>Create Account</h2>
      <form @submit=${this.handleRegister}>
        <div class="form-group">
          <sl-input
            type="text"
            name="name"
            autocomplete="name"
            label="Display Name"
            placeholder="Your name"
            required
            .value=${this.displayName}
            @sl-input=${(e: Event) =>
              (this.displayName = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <div class="form-group">
          <sl-input
            type="email"
            name="email"
            autocomplete="email"
            label="Email"
            placeholder="you@example.com"
            required
            .value=${this.email}
            @sl-input=${(e: Event) =>
              (this.email = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <div class="form-group">
          <sl-input
            type="password"
            name="password"
            autocomplete="new-password"
            label="Password"
            placeholder="Choose a strong password"
            required
            minlength="8"
            .value=${this.password}
            @sl-input=${(e: Event) =>
              (this.password = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <div class="form-group">
          <sl-input
            type="password"
            name="confirm-password"
            autocomplete="new-password"
            label="Confirm Password"
            placeholder="Re-enter your password"
            required
            .value=${this.confirmPassword}
            @sl-input=${(e: Event) =>
              (this.confirmPassword = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>

        <div class="role-selection">
          <span class="role-selection-label"
            >I am a... (select all that apply)</span
          >
          <div class="role-options">
            <div class="role-option">
              <sl-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.DIABETIC)}
                @sl-change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.DIABETIC,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Person with Diabetes
              </sl-checkbox>
            </div>
            <div class="role-description">
              I manage my own blood glucose and CGM data
            </div>

            <div class="role-option">
              <sl-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.MONITOR)}
                @sl-change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.MONITOR,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Friend/Family Monitor
              </sl-checkbox>
            </div>
            <div class="role-description">
              I monitor a family member or friend's data
            </div>

            <div class="role-option">
              <sl-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.CLINICIAN)}
                @sl-change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.CLINICIAN,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Healthcare Professional
              </sl-checkbox>
            </div>
            <div class="role-description">
              I'm a clinician monitoring patients
            </div>
          </div>
        </div>

        <sl-button
          type="submit"
          variant="primary"
          ?loading=${this.isLoading}
          ?disabled=${!navigator.onLine}
        >
          ${this.isLoading ? "Creating account..." : "Create Account"}
        </sl-button>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("login")}
          >Already have an account? Sign in</a
        >
      </div>
    `;
  }

  private renderForgotPassword() {
    return html`
      <h2>Reset Password</h2>
      <p
        style="text-align: center; color: #666; font-size: 0.875rem; margin-bottom: 1rem;"
      >
        Enter your email address and we'll send you a link to reset your
        password.
      </p>
      <form @submit=${this.handleForgotPassword}>
        <div class="form-group">
          <sl-input
            type="email"
            name="email"
            autocomplete="email"
            label="Email"
            placeholder="you@example.com"
            required
            .value=${this.email}
            @sl-input=${(e: Event) =>
              (this.email = (e.target as HTMLInputElement).value)}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>
        <sl-button
          type="submit"
          variant="primary"
          ?loading=${this.isLoading}
          ?disabled=${!navigator.onLine}
        >
          ${this.isLoading ? "Sending..." : "Send Reset Link"}
        </sl-button>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("login")}>Back to sign in</a>
      </div>
    `;
  }

  private switchMode(mode: AuthMode) {
    this.mode = mode;
    this.error = "";
    this.successMessage = "";
    // Don't clear password - might be useful when switching between login/register
  }

  private toggleRole(role: UserRole, checked: boolean) {
    const newRoles = new Set(this.selectedRoles);
    if (checked) {
      newRoles.add(role);
    } else {
      newRoles.delete(role);
    }
    this.selectedRoles = newRoles;
  }

  private async handleLogin(e: Event) {
    e.preventDefault();
    this.error = "";
    this.isLoading = true;

    try {
      const result = await authService.login({
        email: this.email,
        password: this.password,
        staySignedIn: this.staySignedIn,
      });

      if (result.success) {
        // Dispatch event for app to handle
        this.dispatchEvent(
          new CustomEvent("auth-success", { bubbles: true, composed: true }),
        );
      } else {
        this.error = this.getErrorMessage(
          result.errorReason,
          result.errorMessage,
        );
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private async handleRegister(e: Event) {
    e.preventDefault();
    this.error = "";

    // Validate passwords match
    if (this.password !== this.confirmPassword) {
      this.error = "Passwords do not match.";
      return;
    }

    // Validate password strength
    if (this.password.length < 8) {
      this.error = "Password must be at least 8 characters.";
      return;
    }

    // Validate at least one role selected
    if (this.selectedRoles.size === 0) {
      this.error = "Please select at least one role.";
      return;
    }

    this.isLoading = true;

    try {
      const result = await authService.register({
        email: this.email,
        password: this.password,
        displayName: this.displayName,
        roles: Array.from(this.selectedRoles),
      });

      if (result.success) {
        // Dispatch event for app to handle
        this.dispatchEvent(
          new CustomEvent("auth-success", { bubbles: true, composed: true }),
        );
      } else {
        this.error = this.getErrorMessage(
          result.errorReason,
          result.errorMessage,
        );
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private async handleForgotPassword(e: Event) {
    e.preventDefault();
    this.error = "";
    this.successMessage = "";
    this.isLoading = true;

    try {
      const result = await authService.forgotPassword(this.email);

      if (result.success) {
        this.successMessage = "Password reset email sent. Check your inbox.";
      } else {
        this.error = result.message || "Failed to send reset email.";
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private getErrorMessage(
    reason?: LoginFailureReason,
    message?: string,
  ): string {
    switch (reason) {
      case "invalid_credentials":
        return "Invalid email or password.";
      case "account_locked":
        return "Your account has been locked. Please contact support.";
      case "too_many_attempts":
        return "Too many failed attempts. Please try again later.";
      case "email_not_verified":
        return "Please verify your email address before signing in.";
      case "two_factor_required":
        return "Two-factor authentication required.";
      case "password_not_good_enough":
        return "Password does not meet requirements.";
      default:
        return message || "An error occurred. Please try again.";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "login-page": LoginPage;
  }
}
